import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Modal, Spinner } from 'react-bootstrap';
import {
  FaShieldAlt,
  FaUsers,
  FaCog,
  FaKey,
  FaTrash,
  FaExclamationTriangle,
  FaChevronDown,
  FaSave,
  FaSync,
  FaLock,
  FaGlobe,
  FaBell,
  FaTree,
  FaDollarSign,
  FaCopy,
  FaCheck,
  FaImage,
  FaCrown,
  FaStar,
} from 'react-icons/fa';
import api from '../../../utils/api';
import { fetchClub, getCachedClub, removeCachedClub, setCachedClub } from '../utils/clubsCache';
import { useToast } from '../../../components/ui/ToastProvider';
import { YButton } from '../../../ui/YButton';
import { YInput } from '../../../ui/YInput';
import { YTextarea } from '../../../ui/YTextarea';
import { YSelect } from '../../../ui/YSelect';
import { buildEstablishedYearOptions } from '../utils/establishedYearOptions';
import {
  buildAdminOfficerRows,
  getInitials,
  getMemberDisplayName,
  inviteCodeErrorMessage,
  inviteCodeExpiryBadge,
  inviteCodeFallbackLabel,
  inviteCodeSuccessMessage,
  listingToggleErrorMessage,
  listingToggleSuccessMessage,
  upsertOfficerForRole,
} from '../admin/adminDisplayUtils';
import { TransferPresidencyModal } from './TransferPresidencyModal';
import { ClubAdminJoinRequests } from './ClubAdminJoinRequests';
import {
  clearStoredInviteCode,
  loadStoredInviteCode,
  saveStoredInviteCode,
} from '../utils/inviteCodeStorage';
import './ClubAdminPage.css';
import './clubAdminFieldEdition.css';

function buildEditingClub(data) {
  return {
    name: data?.name || '',
    description: data?.description || '',
    allow_area_calculations: data?.allow_area_calculations,
    established_year: data?.established_year ?? '',
    officers: Array.isArray(data?.officers) ? data.officers : [],
    listing_description: data?.listing_description || '',
    location_label: data?.location_label || '',
    annual_dues: data?.annual_dues != null && data.annual_dues !== '' ? String(data.annual_dues) : '',
    max_members: data?.max_members != null && data.max_members !== '' ? String(data.max_members) : '',
    camphouse_label: data?.camphouse_label || '',
  };
}

/** Bootstrap form-switch — same pattern as other yona-web settings toggles. */
function AdminSettingSwitch({ id, checked, onChange, disabled, ariaLabel }) {
  return (
    <Form.Check
      type="switch"
      id={id}
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
      className="club-admin-form-switch"
    />
  );
}

AdminSettingSwitch.propTypes = {
  id: PropTypes.string.isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  ariaLabel: PropTypes.string,
};

export default function ClubAdminPage({ embedded = false, clubId: clubIdProp, onClubUpdated }) {
  const { clubId: clubIdParam } = useParams();
  const clubId = clubIdProp ?? clubIdParam;
  const navigate = useNavigate();
  const { show: showToast } = useToast();
  const logoInputRef = useRef(null);
  const hasLoadedClubRef = useRef(false);

  const [club, setClub] = useState(null);
  const [editingClub, setEditingClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [reassignRole, setReassignRole] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const [areaCalcSaving, setAreaCalcSaving] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [displayInviteCode, setDisplayInviteCode] = useState('');
  const [inviteCodeRegenerating, setInviteCodeRegenerating] = useState(false);
  const [listingToggleSaving, setListingToggleSaving] = useState(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferMemberId, setTransferMemberId] = useState('');
  const [transferSaving, setTransferSaving] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferDate] = useState(() => new Date().toLocaleDateString('en-US'));

  const establishedYearOptions = useMemo(() => buildEstablishedYearOptions(), []);

  const inviteCodeExpiryLabel = useMemo(() => inviteCodeExpiryBadge(club), [club]);
  const inviteCodeDisplayValue = displayInviteCode || inviteCodeFallbackLabel(club);

  const profilesById = useMemo(() => {
    const map = {};
    (club?.members || []).forEach((m) => {
      if (m?.profile) map[m.id] = m.profile;
    });
    return map;
  }, [club]);

  const officerRows = useMemo(
    () =>
      buildAdminOfficerRows({
        club,
        officers: editingClub?.officers || [],
        profilesById,
      }),
    [club, editingClub?.officers, profilesById]
  );

  const loadClub = useCallback(async (force = false) => {
    if (clubId == null || clubId === '') return;
    const cached = getCachedClub(clubId);
    if (!hasLoadedClubRef.current && !cached) setLoading(true);
    try {
      const res = await fetchClub(api, clubId, { force });
      const data = res.data;
      setClub(data);
      setEditingClub(buildEditingClub(data));
      hasLoadedClubRef.current = true;
      onClubUpdated?.(data);
    } catch (_) {
      const fallback = getCachedClub(clubId);
      if (fallback) {
        setClub(fallback);
        setEditingClub(buildEditingClub(fallback));
        hasLoadedClubRef.current = true;
      } else {
        setClub(null);
        setEditingClub(null);
        hasLoadedClubRef.current = false;
      }
    } finally {
      setLoading(false);
    }
  }, [clubId, onClubUpdated]);

  useEffect(() => {
    if (clubId == null || clubId === '') return;
    hasLoadedClubRef.current = false;
    const stored = loadStoredInviteCode(clubId);
    if (stored?.code) {
      setDisplayInviteCode(stored.code);
    } else {
      setDisplayInviteCode('');
    }
  }, [clubId]);

  useEffect(() => {
    if (!club?.id || !club?.has_active_invite_code || !club?.is_president) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/clubs/${club.id}/invite-code/`);
        const code = res.data?.invite_code || '';
        if (cancelled || !code) return;
        saveStoredInviteCode(club.id, { code, expiresAt: res.data?.expires_at || null });
        setDisplayInviteCode(code);
      } catch (_) {
        /* keep cached / fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [club?.id, club?.has_active_invite_code, club?.is_president, club?.invite_code_rotated_at]);

  useEffect(() => {
    loadClub();
  }, [loadClub]);

  const notifyComingSoon = useCallback(
    (feature) => {
      try {
        showToast({
          title: 'Coming soon',
          body: `${feature} isn’t available yet.`,
          variant: 'info',
        });
      } catch (_) { /* no-op */ }
    },
    [showToast]
  );

  const handleSave = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!club?.id || !editingClub) return;
      try {
        setSaving(true);
        const officers = Array.isArray(editingClub.officers)
          ? editingClub.officers
              .filter((o) => o && (o.title || o.name || o.user_id))
              .map((o) => ({
                title: o.title || '',
                name: o.name || '',
                user_id: o.user_id || null,
              }))
          : [];
        const payload = {
          name: editingClub.name,
          description: editingClub.description,
          established_year: editingClub.established_year ? Number(editingClub.established_year) : null,
          officers,
          listing_description: editingClub.listing_description,
          location_label: editingClub.location_label,
          annual_dues: editingClub.annual_dues !== '' ? Number(editingClub.annual_dues) : null,
          max_members: editingClub.max_members !== '' ? Number(editingClub.max_members) : null,
          camphouse_label: editingClub.camphouse_label,
        };
        await api.put(`/clubs/${club.id}/`, payload);
        await loadClub(true);
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 2000);
        try {
          showToast({ title: 'Saved', body: 'Club settings updated.', variant: 'success' });
        } catch (_) { /* no-op */ }
      } catch (_) {
        try {
          showToast({ title: 'Save failed', body: 'Could not update club settings.', variant: 'danger' });
        } catch (_) { /* no-op */ }
      } finally {
        setSaving(false);
      }
    },
    [club?.id, editingClub, loadClub, showToast]
  );

  const handleDeleteClub = useCallback(async () => {
    if (!club?.id || deleteConfirm.trim().toUpperCase() !== 'DELETE') return;
    try {
      setDeleting(true);
      await api.delete(`/clubs/${club.id}/`);
      removeCachedClub(club.id);
      setDeleteOpen(false);
      navigate('/clubs');
    } catch (_) {
      try {
        showToast({ title: 'Delete failed', body: 'Could not delete this club.', variant: 'danger' });
      } catch (_) { /* no-op */ }
    } finally {
      setDeleting(false);
    }
  }, [club?.id, deleteConfirm, navigate, showToast]);

  const handleCopyCode = useCallback(async () => {
    if (!displayInviteCode) {
      notifyComingSoon('Copy invite code — generate a code first');
      return;
    }
    try {
      await navigator.clipboard.writeText(displayInviteCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (_) {
      notifyComingSoon('Copy to clipboard');
    }
  }, [displayInviteCode, notifyComingSoon]);

  const handleRegenerateInviteCode = useCallback(async () => {
    if (!club?.id) return;
    try {
      setInviteCodeRegenerating(true);
      const res = await api.post(`/clubs/${club.id}/regenerate-invite-code/`);
      const code = res.data?.invite_code || '';
      const expiresAt = res.data?.expires_at || null;
      if (code) {
        saveStoredInviteCode(club.id, { code, expiresAt });
        setDisplayInviteCode(code);
      } else {
        clearStoredInviteCode(club.id);
        setDisplayInviteCode('');
      }
      setClub((prev) =>
        prev
          ? {
              ...prev,
              has_active_invite_code: Boolean(code),
              invite_code_expires_at: expiresAt ?? prev.invite_code_expires_at,
            }
          : prev
      );
      onClubUpdated?.({
        ...club,
        has_active_invite_code: Boolean(code),
        invite_code_expires_at: expiresAt ?? club.invite_code_expires_at,
      });
      try {
        showToast({
          title: 'Invite code ready',
          body: inviteCodeSuccessMessage(code),
          variant: 'success',
        });
      } catch (_) { /* no-op */ }
    } catch (err) {
      try {
        showToast({
          title: 'Couldn’t update invite code',
          body: inviteCodeErrorMessage(err),
          variant: 'danger',
        });
      } catch (_) { /* no-op */ }
    } finally {
      setInviteCodeRegenerating(false);
    }
  }, [club, onClubUpdated, showToast]);

  const handleListingToggle = useCallback(
    async (field) => {
      if (!club) return;
      const nextValue = !club[field];
      try {
        setListingToggleSaving(field);
        await api.patch(`/clubs/${club.id}/`, { [field]: nextValue });
        setClub((prev) => (prev ? { ...prev, [field]: nextValue } : prev));
        await loadClub(true);
        try {
          showToast({
            title: 'Saved',
            body: listingToggleSuccessMessage(field, nextValue),
            variant: 'success',
          });
        } catch (_) { /* no-op */ }
      } catch (err) {
        try {
          showToast({
            title: 'Couldn’t save change',
            body: listingToggleErrorMessage(err),
            variant: 'danger',
          });
        } catch (_) { /* no-op */ }
      } finally {
        setListingToggleSaving(null);
      }
    },
    [club, loadClub, showToast]
  );

  const handleReassign = useCallback(
    (member) => {
      if (!reassignRole || !member) return;
      setEditingClub((prev) => ({
        ...prev,
        officers: upsertOfficerForRole(prev?.officers, reassignRole.title, member),
      }));
      setReassignRole(null);
    },
    [reassignRole]
  );

  const handleAreaCalcToggle = useCallback(async () => {
    if (!club) return;
    const nextValue = !club.allow_area_calculations;
    try {
      setAreaCalcSaving(true);
      await api.patch(`/clubs/${club.id}/`, { allow_area_calculations: nextValue });
      setClub((prev) => {
        const next = prev ? { ...prev, allow_area_calculations: nextValue } : prev;
        if (next) setCachedClub(next);
        return next;
      });
      setEditingClub((prev) => (prev ? { ...prev, allow_area_calculations: nextValue } : prev));
      try {
        showToast({
          title: nextValue ? 'Planting Assistant enabled' : 'Planting Assistant disabled',
          body: nextValue
            ? 'Club polygons can be included in Planting Assistant.'
            : 'Club polygons are no longer available in Planting Assistant.',
          variant: 'success',
        });
      } catch (_) { /* no-op */ }
    } catch (_) {
      try {
        showToast({ title: 'Update failed', body: 'Unable to update Planting Assistant access.', variant: 'danger' });
      } catch (_) { /* no-op */ }
    } finally {
      setAreaCalcSaving(false);
    }
  }, [club, showToast]);

  const handleLogoUpload = useCallback(
    async (e) => {
      const file = e?.target?.files?.[0];
      if (!file || !club?.id) return;
      const allowed = ['image/jpeg', 'image/png', 'image/webp'];
      if (!allowed.includes(file.type)) {
        try {
          showToast({ title: 'Invalid type', body: 'Use JPEG, PNG, or WebP.', variant: 'warning' });
        } catch (_) {}
        return;
      }
      setLogoUploading(true);
      try {
        const uploadName = (file.name || 'logo.jpg').replace(/\.(heic|heif)$/i, '.jpg');
        const initRes = await api.post('/assets/init/', {
          filename: uploadName,
          content_type: file.type,
          scope: 'club-logo',
          club_id: club.id,
        });
        const data = initRes.data || {};
        const { upload_url, object_key, reuse, asset: reused } = data;
        if (reuse && reused?.url) {
          await api.patch(`/clubs/${club.id}/`, { logo_object_key: (reused.object_key || object_key) || null });
          await loadClub(true);
          return;
        }
        if (!upload_url || !object_key) {
          try {
            showToast({ title: 'Upload failed', body: 'Could not get upload URL.', variant: 'danger' });
          } catch (_) {}
          return;
        }
        await fetch(upload_url, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
        await api.patch(`/clubs/${club.id}/`, { logo_object_key: object_key });
        await loadClub(true);
        try {
          showToast({ title: 'Logo updated', body: 'Club logo has been set.', variant: 'success' });
        } catch (_) {}
      } catch (err) {
        const msg = err?.response?.data?.error || err?.response?.data?.message || err?.message || 'Upload failed.';
        try {
          showToast({ title: 'Upload failed', body: msg, variant: 'danger' });
        } catch (_) {}
      } finally {
        setLogoUploading(false);
        if (logoInputRef.current) logoInputRef.current.value = '';
      }
    },
    [club?.id, loadClub, showToast]
  );

  const handleLogoRemove = useCallback(async () => {
    if (!club?.id) return;
    try {
      setLogoUploading(true);
      await api.patch(`/clubs/${club.id}/`, { logo_object_key: null });
      await loadClub(true);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to remove logo.';
      try {
        showToast({ title: 'Error', body: msg, variant: 'danger' });
      } catch (_) {}
    } finally {
      setLogoUploading(false);
    }
  }, [club?.id, loadClub, showToast]);

  const eligibleTransferMembers = useMemo(() => {
    if (!club?.members) return [];
    const currentPresId = club?.president?.id || club?.president;
    return club.members.filter((m) => m.id !== currentPresId);
  }, [club]);

  const openTransferModal = useCallback(() => {
    setTransferError('');
    const eligible = eligibleTransferMembers;
    setTransferMemberId(eligible.length > 0 ? String(eligible[0].id) : '');
    setShowTransferModal(true);
  }, [eligibleTransferMembers]);

  const closeTransferModal = useCallback(() => {
    setShowTransferModal(false);
    setTransferSaving(false);
    setTransferError('');
  }, []);

  const handleTransferPresidency = useCallback(
    async (e) => {
      e.preventDefault();
      if (!transferMemberId || !club?.id) {
        setTransferError('Please select a member');
        return;
      }
      const member = club.members.find((m) => String(m.id) === String(transferMemberId));
      const name = member?.username || 'selected member';
      if (!window.confirm(`Transfer presidency to ${name}? This takes effect immediately.`)) {
        return;
      }
      try {
        setTransferSaving(true);
        await api.post(`/clubs/${club.id}/transfer_presidency/`, {
          new_president_id: Number(transferMemberId),
        });
        await loadClub(true);
        closeTransferModal();
        try {
          showToast({ title: 'Presidency transferred', body: `Presidency transferred to ${name}.`, variant: 'success' });
        } catch (_) {}
      } catch (err) {
        setTransferError(err?.response?.data?.error || 'Transfer failed');
        setTransferSaving(false);
      }
    },
    [club, transferMemberId, loadClub, closeTransferModal, showToast]
  );

  const reassignCandidates = useMemo(() => {
    if (!reassignRole || !club?.members) return [];
    const currentName = (reassignRole.name || '').trim().toLowerCase();
    return club.members.filter((m) => {
      const label = getMemberDisplayName(m, m.profile || profilesById[m.id]).toLowerCase();
      return label !== currentName;
    });
  }, [reassignRole, club?.members, profilesById]);

  const privacyToggles = [
    {
      field: 'is_listed',
      icon: FaGlobe,
      label: 'Public Club Listing',
      desc: 'Allow the club to appear in Find a Club search results',
    },
    {
      field: 'is_accepting_members',
      icon: FaUsers,
      label: 'Accepting Members',
      desc: 'Show that your club has openings for new members',
    },
    {
      field: 'is_promoted',
      icon: FaStar,
      label: 'Promoted Listing',
      desc: 'Feature this club at the top of directory results (president opt-in)',
    },
    {
      field: 'require_approval_to_join',
      icon: FaLock,
      label: 'Require Approval to Join',
      desc: 'Directory requests need officer approval (invite codes always join instantly)',
    },
  ];

  const notificationToggles = [
    { key: 'emailDigest', icon: FaBell, label: 'Weekly Email Digest', desc: 'Send members a weekly activity summary' },
    { key: 'smsAlerts', icon: FaBell, label: 'SMS Alerts for Events', desc: 'Send SMS reminders for upcoming events' },
  ];

  const pageClass = `club-admin-page${embedded ? ' club-admin-page--embedded' : ''}`;

  if (loading && !club) {
    return (
      <div className={pageClass}>
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" />
        </div>
      </div>
    );
  }

  if (!club || !editingClub) {
    return (
      <div className={pageClass}>
        <p className="text-muted small mb-0">Unable to load club admin settings.</p>
      </div>
    );
  }

  const acreageDisplay =
    club.acreage != null && !Number.isNaN(Number(club.acreage)) ? String(Number(club.acreage)) : '';

  return (
    <div className={pageClass} data-testid="club-admin-page">
      <div className="club-admin-banner">
        <FaShieldAlt aria-hidden />
        <div>
          <p className="club-admin-banner__title">Admin View — President Only</p>
          <p className="club-admin-banner__sub">
            This section is only visible to the club president. Changes here affect all members.
          </p>
        </div>
      </div>

      <div className="club-admin-grid">
        <div className="club-admin-col">
          <div className="club-admin-card">
            <div className="club-admin-card__header">
              <div className="club-admin-card__header-left">
                <FaUsers aria-hidden />
                <h3 className="club-admin-card__title">Club Officers</h3>
              </div>
            </div>
            {officerRows.map((row) => (
              <div key={row.key} className="club-admin-officer-row">
                <span className="club-admin-officer-avatar" aria-hidden>
                  {row.initials}
                </span>
                <div className="club-admin-officer-meta">
                  <p className="club-admin-officer-name">{row.name}</p>
                  <p className="club-admin-officer-role">
                    {row.title} — Since {row.since}
                  </p>
                </div>
                {row.locked ? (
                  <span className="club-admin-locked">
                    <FaLock aria-hidden />
                    LOCKED
                  </span>
                ) : (
                  <YButton
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="club-admin-reassign-btn"
                    onClick={() => setReassignRole(row)}
                  >
                    Reassign
                    <FaChevronDown aria-hidden className="ms-1" />
                  </YButton>
                )}
              </div>
            ))}
          </div>

          <div className="club-admin-card">
            <Form onSubmit={handleSave}>
              <div className="club-admin-card__header">
                <div className="club-admin-card__header-left">
                  <FaCog aria-hidden />
                  <h3 className="club-admin-card__title">Club Settings</h3>
                </div>
                <YButton
                  type="submit"
                  variant="primary"
                  size="sm"
                  className={`club-admin-save-btn${savedFlash ? ' is-saved' : ''}`}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Saving…
                    </>
                  ) : savedFlash ? (
                    <>
                      <FaCheck aria-hidden className="me-1" />
                      Saved
                    </>
                  ) : (
                    <>
                      <FaSave aria-hidden className="me-1" />
                      Save Changes
                    </>
                  )}
                </YButton>
              </div>

              <div className="club-admin-settings-body">
                <div>
                  <p className="club-admin-section-label">Basic Info</p>
                  <div className="club-admin-fields-grid">
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-name">
                        Club Name
                      </label>
                      <YInput
                        id="club-admin-name"
                        value={editingClub.name}
                        onChange={(e) => setEditingClub((prev) => ({ ...prev, name: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-location">
                        Location label
                      </label>
                      <YInput
                        id="club-admin-location"
                        value={editingClub.location_label}
                        onChange={(e) => setEditingClub((prev) => ({ ...prev, location_label: e.target.value }))}
                        placeholder="e.g. Monroe County, AL"
                      />
                      <p className="club-admin-hint mt-1 mb-0">
                        {club.listing_location_ready
                          ? 'Search shows an approximate area from tracts on the club map. Exact location is never shared.'
                          : 'Draw a hunting boundary on the club map to enable public listing. Fields do not count.'}
                      </p>
                    </div>
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-acreage">
                        Acreage
                      </label>
                      <div className="club-admin-field-with-icon">
                        <FaTree aria-hidden />
                        <YInput
                          id="club-admin-acreage"
                          value={acreageDisplay}
                          readOnly
                          disabled
                          title="Computed from club tracts on the map"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-established">
                        Established
                      </label>
                      <YSelect
                        id="club-admin-established"
                        value={
                          editingClub.established_year != null && editingClub.established_year !== ''
                            ? String(editingClub.established_year)
                            : ''
                        }
                        onChange={(e) => {
                          const val = e.target.value;
                          setEditingClub((prev) => ({
                            ...prev,
                            established_year: val ? Number(val) : '',
                          }));
                        }}
                      >
                        <option value="">No year selected</option>
                        {establishedYearOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </YSelect>
                    </div>
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-max-members">
                        Max Members
                      </label>
                      <YInput
                        id="club-admin-max-members"
                        type="number"
                        min={1}
                        value={editingClub.max_members}
                        onChange={(e) => setEditingClub((prev) => ({ ...prev, max_members: e.target.value }))}
                        placeholder="e.g. 12"
                      />
                    </div>
                    <div>
                      <label className="club-admin-field-label" htmlFor="club-admin-dues">
                        Annual Dues ($)
                      </label>
                      <div className="club-admin-field-with-icon">
                        <FaDollarSign aria-hidden />
                        <YInput
                          id="club-admin-dues"
                          type="number"
                          min={0}
                          step="0.01"
                          value={editingClub.annual_dues}
                          onChange={(e) => setEditingClub((prev) => ({ ...prev, annual_dues: e.target.value }))}
                          placeholder="e.g. 850"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="mt-3">
                    <label className="club-admin-field-label" htmlFor="club-admin-description">
                      Club Description
                    </label>
                    <YTextarea
                      id="club-admin-description"
                      rows={3}
                      value={editingClub.description}
                      onChange={(e) => setEditingClub((prev) => ({ ...prev, description: e.target.value }))}
                    />
                  </div>
                  <div className="mt-3">
                    <label className="club-admin-field-label" htmlFor="club-admin-listing-description">
                      Directory listing blurb
                    </label>
                    <YTextarea
                      id="club-admin-listing-description"
                      rows={2}
                      value={editingClub.listing_description}
                      onChange={(e) => setEditingClub((prev) => ({ ...prev, listing_description: e.target.value }))}
                      placeholder="Short quote or pitch shown on Find a Club cards"
                    />
                  </div>
                  <div className="mt-3">
                    <label className="club-admin-field-label" htmlFor="club-admin-camphouse">
                      Camphouse
                    </label>
                    <YInput
                      id="club-admin-camphouse"
                      value={editingClub.camphouse_label}
                      onChange={(e) => setEditingClub((prev) => ({ ...prev, camphouse_label: e.target.value }))}
                      placeholder="e.g. Heated lodge · full kitchen"
                    />
                  </div>
                  <div className="mt-3">
                    <p className="club-admin-section-label">Club Logo</p>
                    <div className="club-admin-logo-row">
                      {club.logo_url ? (
                        <img src={club.logo_url} alt="" />
                      ) : (
                        <span className="club-admin-hint mb-0">
                          <FaImage aria-hidden className="me-1" />
                          No logo uploaded
                        </span>
                      )}
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="d-none"
                        onChange={handleLogoUpload}
                      />
                      <YButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={logoUploading}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {logoUploading ? <Spinner animation="border" size="sm" className="me-1" /> : null}
                        {club.logo_url ? 'Change' : 'Upload'}
                      </YButton>
                      {club.logo_url ? (
                        <YButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={logoUploading}
                          onClick={handleLogoRemove}
                        >
                          Remove
                        </YButton>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="club-admin-settings-divider">
                  <p className="club-admin-section-label">Privacy &amp; Visibility</p>
                  <div className="d-flex flex-column gap-2">
                    {privacyToggles.map(({ field, icon: Icon, label, desc }) => (
                      <div key={field} className="club-admin-toggle-row">
                        <div className="d-flex gap-2 club-admin-toggle-row__text">
                          <Icon aria-hidden />
                          <div>
                            <p className="club-admin-toggle-row__label">{label}</p>
                            <p className="club-admin-toggle-row__desc">{desc}</p>
                          </div>
                        </div>
                        <AdminSettingSwitch
                          id={`club-admin-toggle-${field}`}
                          checked={Boolean(club[field])}
                          onChange={() => handleListingToggle(field)}
                          disabled={
                            listingToggleSaving === field ||
                            (field === 'is_listed' && !club.listing_location_ready && !club.is_listed) ||
                            (field === 'is_promoted' && !club.is_listed && !club.is_promoted)
                          }
                          ariaLabel={label}
                        />
                      </div>
                    ))}
                    <div className="club-admin-toggle-row">
                      <div className="d-flex gap-2 club-admin-toggle-row__text">
                        <FaTree aria-hidden />
                        <div>
                          <p className="club-admin-toggle-row__label">Planting Assistant Access</p>
                          <p className="club-admin-toggle-row__desc">
                            Allow club polygons in the Planting Assistant tool
                          </p>
                        </div>
                      </div>
                      <AdminSettingSwitch
                        id="club-admin-toggle-area-calc"
                        checked={Boolean(club.allow_area_calculations)}
                        onChange={() => handleAreaCalcToggle()}
                        disabled={areaCalcSaving}
                        ariaLabel="Planting Assistant access"
                      />
                    </div>
                  </div>
                </div>

                <div className="club-admin-settings-divider">
                  <p className="club-admin-section-label">Notifications</p>
                  <div className="d-flex flex-column gap-2">
                    {notificationToggles.map(({ key, icon: Icon, label, desc }) => (
                      <div key={key} className="club-admin-toggle-row">
                        <div className="d-flex gap-2 club-admin-toggle-row__text">
                          <Icon aria-hidden />
                          <div>
                            <p className="club-admin-toggle-row__label">{label}</p>
                            <p className="club-admin-toggle-row__desc">{desc}</p>
                          </div>
                        </div>
                        <AdminSettingSwitch
                          id={`club-admin-toggle-${key}`}
                          checked={false}
                          onChange={() => notifyComingSoon(label)}
                          ariaLabel={label}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Form>
          </div>
        </div>

        <div className="club-admin-col">
          <div className="club-admin-card club-admin-side-card">
            <div className="club-admin-card__header-left mb-3">
              <FaKey aria-hidden />
              <h3 className="club-admin-card__title">Invite Code</h3>
            </div>
            <div className="club-admin-invite-code">
              <div className="club-admin-invite-code__main">
                <span
                  className={`club-admin-invite-code__value${displayInviteCode ? '' : ' club-admin-invite-code__value--muted'}`}
                >
                  {inviteCodeDisplayValue}
                </span>
                {inviteCodeExpiryLabel ? (
                  <span className="club-admin-invite-code__expiry">{inviteCodeExpiryLabel}</span>
                ) : null}
              </div>
              <button
                type="button"
                className="club-admin-invite-copy"
                onClick={handleCopyCode}
                aria-label="Copy invite code"
                disabled={!displayInviteCode}
              >
                {codeCopied ? <FaCheck aria-hidden className="text-success" /> : <FaCopy aria-hidden />}
              </button>
            </div>
            <p className="club-admin-hint">
              Share this code for instant join (30-day expiry). Regenerate to rotate the code anytime.
            </p>
            <YButton
              type="button"
              variant="secondary"
              size="sm"
              className="w-100"
              disabled={inviteCodeRegenerating}
              onClick={handleRegenerateInviteCode}
            >
              {inviteCodeRegenerating ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSync aria-hidden className="me-2" />}
              {club.has_active_invite_code ? 'Regenerate Code' : 'Generate Code'}
            </YButton>
          </div>

          <div className="club-admin-card club-admin-side-card">
            <ClubAdminJoinRequests clubId={clubId} />
          </div>

          <div className="club-admin-card club-admin-side-card">
            <div className="club-admin-card__header-left mb-3">
              <FaTree aria-hidden />
              <h3 className="club-admin-card__title">Season Control</h3>
            </div>
            <div className="club-admin-season-status">
              <span>2025–26 SEASON ACTIVE</span>
              <span className="club-admin-season-dot" aria-hidden />
            </div>
            <div className="club-admin-side-actions">
              <YButton type="button" variant="secondary" size="sm" onClick={() => notifyComingSoon('Archive season')}>
                Archive Season
              </YButton>
              <YButton
                type="button"
                variant="secondary"
                size="sm"
                className="text-danger"
                onClick={() => notifyComingSoon('Close season')}
              >
                Close Season
              </YButton>
            </div>
          </div>

          <div className="club-admin-card club-admin-side-card club-admin-danger">
            <div className="club-admin-card__header-left mb-3">
              <FaExclamationTriangle aria-hidden />
              <h3 className="club-admin-card__title">Danger Zone</h3>
            </div>
            <p className="club-admin-hint">
              Transfer presidency or permanently delete this club and all associated data.
            </p>
            <div className="club-admin-danger-actions">
              <YButton type="button" variant="secondary" size="sm" className="w-100" onClick={openTransferModal}>
                <FaCrown aria-hidden className="me-2" />
                Transfer Presidency
              </YButton>
              <YButton type="button" variant="secondary" size="sm" className="text-danger w-100" onClick={() => setDeleteOpen(true)}>
                <FaTrash aria-hidden className="me-2" />
                Delete Club
              </YButton>
            </div>
          </div>
        </div>
      </div>

      <Modal
        show={Boolean(reassignRole)}
        onHide={() => setReassignRole(null)}
        centered
        dialogClassName="club-admin-dialog"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reassign {reassignRole?.title || 'Officer'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="club-admin-reassign-list">
            {reassignCandidates.length === 0 ? (
              <p className="text-muted small mb-0">No other members available.</p>
            ) : (
              reassignCandidates.map((member) => {
                const name = getMemberDisplayName(member, member.profile || profilesById[member.id]);
                return (
                  <button
                    key={member.id}
                    type="button"
                    className="club-admin-reassign-item"
                    onClick={() => handleReassign(member)}
                  >
                    <span className="club-admin-officer-avatar" aria-hidden>
                      {getInitials(name)}
                    </span>
                    <span>{name}</span>
                  </button>
                );
              })
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={deleteOpen} onHide={() => { setDeleteOpen(false); setDeleteConfirm(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-danger d-flex align-items-center gap-2">
            <FaTrash aria-hidden />
            Delete Club
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small mb-3">
            Are you sure? This will permanently delete <strong>{club.name}</strong> and all member data, hunt logs, and
            records. This cannot be undone.
          </p>
          <YInput
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder='Type "DELETE" to confirm'
            aria-label="Type DELETE to confirm"
            className="club-admin-delete-confirm"
          />
        </Modal.Body>
        <Modal.Footer>
          <YButton type="button" variant="secondary" onClick={() => { setDeleteOpen(false); setDeleteConfirm(''); }}>
            Cancel
          </YButton>
          <YButton
            type="button"
            variant="danger"
            disabled={deleting || deleteConfirm.trim().toUpperCase() !== 'DELETE'}
            onClick={handleDeleteClub}
          >
            {deleting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Deleting…
              </>
            ) : (
              <>
                <FaTrash aria-hidden className="me-2" />
                Delete Forever
              </>
            )}
          </YButton>
        </Modal.Footer>
      </Modal>

      <TransferPresidencyModal
        show={showTransferModal}
        onHide={closeTransferModal}
        club={club}
        date={transferDate}
        members={eligibleTransferMembers}
        selectedMemberId={transferMemberId}
        setSelectedMemberId={setTransferMemberId}
        onSubmit={handleTransferPresidency}
        saving={transferSaving}
        error={transferError}
      />
    </div>
  );
}

ClubAdminPage.propTypes = {
  embedded: PropTypes.bool,
  clubId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClubUpdated: PropTypes.func,
};
